import type { Vec2 } from '../types';
import { INPUT_ZONES } from './zones';
import { PINBALL_ORIENTATION } from './pinball';

/**
 * Drag-based tilt for mobile devices when motion permission is denied.
 * Drag in the upper portion of screen to tilt; release to spring back to center.
 * Bottom portion reserved for flipper controls.
 */
export class DragTiltInput {
  readonly tilt: Vec2 = { x: 0, y: 0 };

  /** Max drag distance in pixels for full tilt */
  maxDragDistance = 100;
  /** Spring-back speed (units per second toward zero) */
  springBack = 8.0;

  private dragging = false;
  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private currentY = 0;
  private pointerId: number | null = null;
  private element: HTMLElement | null = null;

  private boundPointerDown = this.handlePointerDown.bind(this);
  private boundPointerMove = this.handlePointerMove.bind(this);
  private boundPointerUp = this.handlePointerUp.bind(this);
  private boundPointerCancel = this.handlePointerCancel.bind(this);

  attach(element: HTMLElement): void {
    this.element = element;

    // Prevent browser gestures from stealing touches
    element.style.touchAction = 'none';

    element.addEventListener('pointerdown', this.boundPointerDown);
    element.addEventListener('pointermove', this.boundPointerMove);
    element.addEventListener('pointerup', this.boundPointerUp);
    element.addEventListener('pointercancel', this.boundPointerCancel);
  }

  private handlePointerDown(e: PointerEvent): void {
    // Only track one pointer for tilt
    if (this.pointerId !== null) return;
    if (!this.element) return;

    const rect = this.element.getBoundingClientRect();
    const y = e.clientY - rect.top;

    // Ignore the bottom flipper region
    if (y >= rect.height * INPUT_ZONES.tiltZoneBottom) return;

    this.pointerId = e.pointerId;
    this.dragging = true;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.currentX = e.clientX;
    this.currentY = e.clientY;

    // Capture pointer for reliable tracking
    this.element.setPointerCapture(e.pointerId);
  }

  private handlePointerMove(e: PointerEvent): void {
    if (e.pointerId !== this.pointerId) return;

    this.currentX = e.clientX;
    this.currentY = e.clientY;

    // Calculate drag delta and normalize to [-1, 1]
    const dx = this.currentX - this.startX;
    const dy = this.currentY - this.startY;

    // Apply pinball orientation sign for X axis
    this.tilt.x = Math.max(-1, Math.min(1, PINBALL_ORIENTATION.tiltXSign * dx / this.maxDragDistance));
    this.tilt.y = Math.max(-1, Math.min(1, dy / this.maxDragDistance));
  }

  private handlePointerUp(e: PointerEvent): void {
    if (e.pointerId !== this.pointerId) return;
    this.endDrag();
  }

  private handlePointerCancel(e: PointerEvent): void {
    if (e.pointerId !== this.pointerId) return;
    this.endDrag();
  }

  private endDrag(): void {
    this.dragging = false;
    this.pointerId = null;
    // Tilt will spring back in update()
  }

  /** Call each frame to animate spring-back */
  update(dt: number): void {
    if (this.dragging) return;

    // Spring back toward center
    this.tilt.x -= this.tilt.x * this.springBack * dt;
    this.tilt.y -= this.tilt.y * this.springBack * dt;

    // Snap to zero when close
    if (Math.abs(this.tilt.x) < 0.01) this.tilt.x = 0;
    if (Math.abs(this.tilt.y) < 0.01) this.tilt.y = 0;
  }

  dispose(): void {
    if (this.element) {
      this.element.removeEventListener('pointerdown', this.boundPointerDown);
      this.element.removeEventListener('pointermove', this.boundPointerMove);
      this.element.removeEventListener('pointerup', this.boundPointerUp);
      this.element.removeEventListener('pointercancel', this.boundPointerCancel);
      this.element = null;
    }
    this.pointerId = null;
    this.dragging = false;
  }
}
